import { MenuItemProps, Stack, Typography } from "@mui/material";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { GetAllEmailTemplatesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getAllEmailTemplatesRPC";
import { GetEmailTemplateDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailTemplateDetailsRPC";
import {
  AutoCompleteInputField,
  AutoCompleteItem,
  LoadingIndicator,
  Snackbar,
  SnackbarProps,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { Instance } from "mobx-state-tree";
import { observer } from "mobx-react";
import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import {
  processProjectParams,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import {
  useCreateEmailTemplateStore,
  useEmailTemplateDetailsStore,
  useEmailTemplatesStore,
  useUpdateEmailTemplateStore,
} from "../../../emailTemplates/stores/Hooks";
import {
  EmailTemplateContentRTE,
  MESSAGE_FIELD_HEIGHT,
} from "../../../emailTemplates/components/inputs/EmailTemplateContentRTE";
import { BoundedNameModel } from "../../../models/BoundedNameModel";
import { CreateNewTemplateDialog } from "../dialogs/CreateNewTemplateDialog";
import { UpdateCurrentTemplateDialog } from "../dialogs/UpdateCurrentTemplateDialog";
import { EmailTemplateContentModel } from "../../../emailTemplates/models/EmailTemplateContentModel";
import { getEmailCycleMessageCustomizationValidationErrorMessage } from "../../utils/getEmailCycleMessageCustomizationValidationErrorMessage";
import { ReadOnlyFieldContainer } from "../inputs/ReadOnlyFieldContainer";
import { MenuButton } from "../../../../components/buttons/MenuButton";
import { CampaignEmailPlaceholderPicker } from "../../../emailTemplates/components/inputs/CampaignEmailPlaceholderPicker";

interface EmailCycleMessageCustomizationFormProps {
  templateId: string | null;
  setTemplateId: (templateId: string | null) => void;
  templateName: Instance<typeof BoundedNameModel>;
  templateContent: Instance<typeof EmailTemplateContentModel>;
  readOnly?: boolean;
  enableRTEPopupZIndexFix?: boolean;
}

const TEMPLATE_NAME_FIELD_MAX_WIDTH = "411px";

export const EmailCycleMessageCustomizationForm: React.FC<EmailCycleMessageCustomizationFormProps> =
  observer(
    ({
      templateId,
      setTemplateId,
      templateName,
      templateContent,
      readOnly = false,
      enableRTEPopupZIndexFix = false,
    }) => {
      const [unexpectedErrorDialogMessage, setUnexpectedErrorDialogMessage] =
        useState<string>();
      const [openCreateNewTemplateDialog, setOpenCreateNewTemplateDialog] =
        useState(false);
      const [
        openUpdateCurrentTemplateDialog,
        setOpenUpdateCurrentTemplateDialog,
      ] = useState(false);
      const [snackbarConfig, setSnackbarConfig] =
        useState<Pick<SnackbarProps, "message" | "severity">>();
      const isEmailTemplatesFetched = useRef(false);

      const { t } = useTranslation();
      const spacing = useSpacing();
      const typography = useTypography();
      const tokens = useFoundationColorTokens();
      const navigateTo = useRouteNavigator();
      const templateNameFieldId = useId();
      const [searchParams] = useSearchParams();
      const projectId = searchParams.get("projectId");

      const emailTemplatesStore = useEmailTemplatesStore();
      const emailTemplateDetailsStore = useEmailTemplateDetailsStore();
      const createEmailTemplateStore = useCreateEmailTemplateStore();
      const updateEmailTemplateStore = useUpdateEmailTemplateStore();

      // Fetch email templates when user focus on template field
      useEffect(() => {
        if (readOnly) return;

        const templateNameFieldDOMElement =
          document.getElementById(templateNameFieldId);
        if (!templateNameFieldDOMElement) return;

        const handleTemplateNameFieldFocusChange = async (): Promise<void> => {
          if (isEmailTemplatesFetched.current) return;

          await processProjectParams(async (currentProjectId) => {
            await emailTemplatesStore
              .getAllEmailTemplates(currentProjectId)
              .then(() => {
                if (emailTemplatesStore.getAllEmailTemplatesRPCError) {
                  switch (emailTemplatesStore.getAllEmailTemplatesRPCError) {
                    case GetAllEmailTemplatesRPC.RPCError.InvalidProjectId:
                      navigateTo.pageNotFound();
                      break;
                    default:
                      emailTemplatesStore.getAllEmailTemplatesRPCError satisfies never;
                  }
                  return;
                }
                isEmailTemplatesFetched.current = true;
              })
              .catch(() =>
                setUnexpectedErrorDialogMessage(
                  t("emailCycles.getAllEmailTemplatesUnexpectedErrorMessage"),
                ),
              );
          }, navigateTo);
        };

        templateNameFieldDOMElement.addEventListener(
          "focus",
          handleTemplateNameFieldFocusChange,
        );

        return () =>
          templateNameFieldDOMElement.removeEventListener(
            "focus",
            handleTemplateNameFieldFocusChange,
          );
      }, []);

      const templateOptions: AutoCompleteItem[] =
        emailTemplatesStore.emailTemplates.map((emailTemplate) => ({
          label: emailTemplate.templateName.name,
          id: emailTemplate.id,
        }));
      const templateValue: AutoCompleteItem | null | undefined = templateId
        ? templateOptions.find((option) => option.id === templateId)
        : null;
      const validationErrors =
        getEmailCycleMessageCustomizationValidationErrorMessage(t, {
          body: templateContent.validationError,
        });

      const saveTemplateMenuOptions: MenuItemProps[] = [
        {
          children: t("emailCycles.newTemplate"),
          onClick: (): void => {
            if (projectId === null) {
              navigateTo.pageNotFound();
              return;
            }
            createEmailTemplateStore.emailTemplateContent.setContent(
              templateContent.content,
            );
            setOpenCreateNewTemplateDialog(true);
          },
        },
      ];
      if (templateId) {
        saveTemplateMenuOptions.push({
          children: t("emailCycles.updateTemplate"),
          onClick: (): void => {
            updateEmailTemplateStore.setTemplateId(templateId);
            updateEmailTemplateStore.emailTemplateName.setName(
              templateName.name,
            );
            updateEmailTemplateStore.emailTemplateContent.setContent(
              templateContent.content,
            );
            setOpenUpdateCurrentTemplateDialog(true);
          },
        });
      }

      const handleTemplateChange = async (
        value: AutoCompleteItem | null,
      ): Promise<void> => {
        setTemplateId(value?.id ?? null);
        templateName.setName(value?.label ?? EMPTY_CHARACTER);

        if (!value?.id) return;

        try {
          await emailTemplateDetailsStore.getEmailTemplateDetails(
            new LeoUUID(value.id),
          );

          switch (emailTemplateDetailsStore.getEmailTemplateDetailsRPCError) {
            case null:
              templateContent.setContent(
                emailTemplateDetailsStore.emailTemplateContent.content,
              );
              break;
            case GetEmailTemplateDetailsRPC.RPCError.InvalidTemplateId:
              navigateTo.pageNotFound();
              break;
            default:
              emailTemplateDetailsStore.getEmailTemplateDetailsRPCError satisfies never;
          }
        } catch {
          setUnexpectedErrorDialogMessage(
            t("emailCycles.getAllEmailTemplatesUnexpectedErrorMessage"),
          );
        }
      };

      return (
        <>
          {snackbarConfig && (
            <Snackbar
              onClose={() => setSnackbarConfig(undefined)}
              isDismissible
              isOpen
              {...snackbarConfig}
            />
          )}

          <Stack gap={spacing.spaceXL}>
            {/* Title */}
            <Typography {...typography.sh2}>
              {t("emailTemplates.messageCustomization")}
            </Typography>

            {/* Form */}
            <Stack gap={spacing.spaceLG}>
              {/* Template field */}
              {readOnly ? (
                <Stack gap={spacing.spaceXS}>
                  {templateName.name && (
                    <Stack
                      width="100%"
                      maxWidth={TEMPLATE_NAME_FIELD_MAX_WIDTH}
                    >
                      <ReadOnlyFieldContainer
                        label={t("emailCycles.templateNameLabel")}
                      >
                        {templateName.name}
                      </ReadOnlyFieldContainer>
                    </Stack>
                  )}
                  <Stack width="100%" maxWidth={TEMPLATE_NAME_FIELD_MAX_WIDTH}>
                    <CampaignEmailPlaceholderPicker
                      projectId={projectId}
                      disabled
                    />
                  </Stack>
                </Stack>
              ) : (
                <Stack gap={spacing.spaceXS}>
                  <Stack direction="row" gap={spacing.spaceXS} alignItems="end">
                    <Stack
                      width="100%"
                      maxWidth={TEMPLATE_NAME_FIELD_MAX_WIDTH}
                    >
                      <AutoCompleteInputField
                        id={templateNameFieldId}
                        label={t("emailCycles.templateNameLabel")}
                        options={templateOptions}
                        value={templateValue}
                        onChange={handleTemplateChange}
                        placeholder={t("emailCycles.templateNamePlaceholder")}
                        loading={
                          emailTemplatesStore.loadingAllEmailTemplates ||
                          emailTemplateDetailsStore.loadingEmailTemplateDetails
                        }
                        loadingText={t("emailCycles.loadingEmailTemplatesText")}
                        noOptionsText={t(
                          "emailCycles.emailTemplatesNoOptionsText",
                        )}
                        disabled={
                          emailTemplateDetailsStore.loadingEmailTemplateDetails
                        }
                      />
                    </Stack>
                    <Stack marginBottom={spacing.spaceXXS}>
                      <MenuButton
                        button={{
                          name: "saveTemplateMenuButton",
                          label: t("emailCycles.saveTemplate"),
                          size: "large",
                          variant: "outlined-color",
                          disabled:
                            !templateContent.content ||
                            emailTemplateDetailsStore.loadingEmailTemplateDetails,
                        }}
                        menuItems={saveTemplateMenuOptions.map(
                          ({ children, ...menuItemProps }) => ({
                            children: (
                              <Typography {...typography.b1}>
                                {children}
                              </Typography>
                            ),
                            ...menuItemProps,
                          }),
                        )}
                      />
                    </Stack>
                  </Stack>
                  <Stack width="100%" maxWidth={TEMPLATE_NAME_FIELD_MAX_WIDTH}>
                    <CampaignEmailPlaceholderPicker
                      projectId={projectId}
                      onInvalidProjectId={() => navigateTo.pageNotFound()}
                    />
                  </Stack>
                </Stack>
              )}

              {/* Message field */}
              {emailTemplateDetailsStore.loadingEmailTemplateDetails ? (
                <Stack height={MESSAGE_FIELD_HEIGHT} justifyContent="start">
                  <Stack width="fit-content">
                    <LoadingIndicator
                      variant="container"
                      loadingText={t(
                        "emailCycles.loadingEmailTemplateContentText",
                      )}
                      isLoading
                    />
                  </Stack>
                </Stack>
              ) : (
                <Stack gap={spacing.spaceXXS} width="100%" height="100%">
                  {readOnly && (
                    <Typography color={tokens.label} {...typography.s2}>
                      {t("emailTemplates.contentLabel")}
                    </Typography>
                  )}
                  <EmailTemplateContentRTE
                    mode={readOnly ? "preview" : "editable"}
                    initialValue={templateContent.content}
                    onChange={templateContent.setContent}
                    error={!!validationErrors.body}
                    helperText={validationErrors.body}
                    rteFileService={templateContent.rteFileService}
                    enableRTEPopupZIndexFix={enableRTEPopupZIndexFix}
                    hidePreviewEditToggler={readOnly}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>

          {!readOnly && (
            <>
              {projectId !== null && (
                <CreateNewTemplateDialog
                  open={openCreateNewTemplateDialog}
                  projectId={projectId}
                  closeDialog={(config) => {
                    setOpenCreateNewTemplateDialog(false);
                    createEmailTemplateStore.resetStore();
                    if (config) {
                      setSnackbarConfig({
                        message: config.message,
                        severity: config.error ? "error" : "success",
                      });
                    }
                  }}
                />
              )}

              <UpdateCurrentTemplateDialog
                open={openUpdateCurrentTemplateDialog}
                closeDialog={(config) => {
                  setOpenUpdateCurrentTemplateDialog(false);
                  updateEmailTemplateStore.resetStore();
                  if (config) {
                    setSnackbarConfig({
                      message: config.message,
                      severity: config.error ? "error" : "success",
                    });
                  }
                }}
                resetIsEmailTemplatesFetchedValue={() =>
                  (isEmailTemplatesFetched.current = false)
                }
              />
            </>
          )}

          {unexpectedErrorDialogMessage && (
            <UnexpectedErrorDialog
              message={unexpectedErrorDialogMessage}
              onClickDone={() => setUnexpectedErrorDialogMessage(undefined)}
            />
          )}
        </>
      );
    },
  );
